import type { Freshness } from './api'

// Readers had to press "Refresh data" almost every time they opened the
// dashboard (Casey, 2026-10-20): deals and freee sync every 2h, but the forecast
// syncs once a day and payroll once a month, so sheet edits made since then are
// not in the database until someone pulls. These helpers decide when the
// dashboard pulls for them, and word the "updating" state, so the decision is
// testable without rendering App.

export const AUTO_REFRESH_MAX_AGE_MS = 15 * 60_000
export const SLOW_REFRESH_SECONDS = 20

// Sources the Refresh mutation never runs (refresh.resolver.ts refreshes only
// deal-ledger, payroll, forecast-snapshot, freee and gospel-ap). The weekly
// cash-forecast copy runs at 06:00 JST and at deploy, so its lastRunAt is
// usually hours old and, counted here, would trigger a full ingest on nearly
// every open that Refresh could never clear.
const NOT_REFRESHABLE_JOBS: ReadonlySet<string> = new Set(['cash-forecast-weekly'])

// Whether opening the dashboard should run Refresh. It looks at lastRunAt (the
// last attempt), not lastSucceededAt: a source that is broken, say a payroll
// sheet whose totals don't add up, would otherwise make every open retry a
// refresh that is guaranteed to fail.
export function shouldAutoRefresh(
  allSources: Freshness[],
  now: number,
  maxAgeMs: number = AUTO_REFRESH_MAX_AGE_MS,
): boolean {
  const freshness = allSources.filter((source) => !NOT_REFRESHABLE_JOBS.has(source.job))
  const ageOf = (source: Freshness): number | null => {
    if (source.lastRunAt === null) return null
    const at = Date.parse(source.lastRunAt)
    return Number.isNaN(at) ? null : now - at
  }
  // A pull already in flight (the 2-hourly schedule, or a colleague's Refresh)
  // means skip: a second concurrent ingest is the risky case. freee rotates its
  // single-use refresh token, and overlapping deal syncs can collide on the
  // line_key unique constraint. A 'running' row older than the window is stuck,
  // not running, and must not block forever.
  const running = freshness.some((source) => {
    const age = ageOf(source)
    return source.lastRunStatus === 'running' && age !== null && age < maxAgeMs
  })
  if (running) return false
  if (freshness.length === 0) return true
  // Never run or unparsable counts as old; a future timestamp (client clock
  // behind the server) has a negative age and counts as recent.
  return freshness.some((source) => {
    const age = ageOf(source)
    return age === null || age >= maxAgeMs
  })
}

export function formatElapsed(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds))
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

export function updatingDetail(elapsedSeconds: number): string {
  return elapsedSeconds >= SLOW_REFRESH_SECONDS
    ? 'Reading every source can take up to half a minute. Your numbers stay on screen meanwhile.'
    : "You're seeing the last saved numbers. They'll update here when this finishes."
}

// API Gateway stops waiting at 30s while the ingest carries on. Its own 504
// carries no CORS header (CORS lives in the app, terraform/lambda.tf), so the
// browser usually reports a network failure instead: a TypeError this late is
// read as that timeout, not as the network being down.
export const REFRESH_TIMEOUT_GUESS_MS = 25_000

// Shown when the Refresh call itself throws. The pointer to the last saved
// numbers is added only when some are on screen: a failed first load leaves the
// page empty while Refresh is still offered.
export function refreshErrorMessage(
  cause: unknown,
  elapsedMs: number,
  showingData: boolean,
): string {
  // Trailing full stop dropped: the message is embedded mid-sentence below.
  const message = (cause instanceof Error ? cause.message : String(cause)).replace(/\.$/, '')
  const timedOut =
    message === 'API returned 504' ||
    (cause instanceof TypeError && elapsedMs >= REFRESH_TIMEOUT_GUESS_MS)
  const text = timedOut
    ? 'The update took longer than the server waits for. It may still finish in the background, so press Reload in a minute to check.'
    : `Couldn't get the latest numbers: ${message}.`
  return showingData ? `${text} The numbers below are the last saved ones.` : text
}

// An automatic refresh stays quiet only when every source landed; a manual
// click always reports, since the user asked for the result.
export function isQuietOutcome(
  mode: 'manual' | 'auto',
  outcome: readonly { status: string }[],
): boolean {
  return mode === 'auto' && outcome.every((r) => r.status === 'succeeded')
}

// How the reload after a refresh went; 'signed-out' means the session was
// rejected and the app has already returned to the sign-in screen.
export type ReloadResult = 'loaded' | 'failed' | 'signed-out'

// What a finished refresh leaves on screen. Loud outcomes keep their banner even
// when the reload failed (the per-source result still matters), but never after
// a sign-out: that already cleared the screen, and a banner set now would
// outlive the session.
export function refreshNotice(
  mode: 'manual' | 'auto',
  outcome: readonly { status: string }[],
  reload: ReloadResult,
): 'banner' | 'updated-note' | 'none' {
  if (reload === 'signed-out') return 'none'
  if (!isQuietOutcome(mode, outcome)) return 'banner'
  return reload === 'loaded' ? 'updated-note' : 'none'
}
